import React, { useCallback, useEffect, useState } from "react";
import { shareApi } from "../../lib/api";
import { Card } from "../shared";
import { MessageCircleQuestion, Send, Trash2, CheckCircle2, Globe, Sparkles } from "lucide-react";
import { toast } from "sonner";

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

export function QATab({ shareId, sessionId, investor }) {
  const [text, setText] = useState("");
  const [data, setData] = useState({ mine: [], faq: [] });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => shareApi.questions(shareId, sessionId).then(setData).catch(() => {}), [shareId, sessionId]);
  useEffect(() => { load(); }, [load]);

  const ask = async () => {
    if (text.trim().length < 3) return toast.error("Write your question first");
    setBusy(true);
    try {
      await shareApi.ask(shareId, sessionId, text.trim());
      setText("");
      toast.success("Question sent — the sponsor has been notified and will reply here" + (investor.email ? " and by email" : ""));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid g2">
      <div>
        <Card title="Ask the Sponsor" testid="ir-qa-form">
          <textarea className="inp" rows={4} placeholder="e.g. How much of revenue comes from the top 5 customers? What happens to the seller note if EBITDA misses?" value={text} onChange={(e) => setText(e.target.value)} data-testid="ir-qa-input" />
          <button className="btn btn-primary" style={{ width: "100%", marginTop: 8 }} onClick={ask} disabled={busy} data-testid="ir-qa-send"><Send size={13} style={{ marginRight: 6 }} />{busy ? "Sending…" : "Send question"}</button>
          <div className="ir-fine">Questions are private to you unless the sponsor chooses to publish the answer to all investors.</div>
        </Card>
        <Card title="Your Questions" className="ir-gap" testid="ir-qa-mine">
          {data.mine.length === 0 && <div className="bullet">You haven't asked anything yet.</div>}
          {data.mine.map((q) => (
            <div className="qa-item" key={q.id} data-testid={`ir-q-${q.id}`}>
              <div className="qa-q"><MessageCircleQuestion size={13} />{q.question}<span className="qa-when">{fmtWhen(q.createdAt)}</span></div>
              {q.answer ? <div className="qa-a" data-testid={`ir-a-${q.id}`}><CheckCircle2 size={13} /><div>{q.answer}<span className="qa-when">Answered {fmtWhen(q.answeredAt)}</span></div></div>
                : <div className="qa-pending">Awaiting sponsor reply</div>}
            </div>
          ))}
        </Card>
      </div>
      <Card title="Answered for All Investors" testid="ir-qa-faq">
        {data.faq.length === 0 && <div className="bullet">No published answers yet.</div>}
        {data.faq.map((q) => (
          <div className="qa-item" key={q.id}>
            <div className="qa-q"><MessageCircleQuestion size={13} />{q.question}</div>
            <div className="qa-a"><CheckCircle2 size={13} /><div>{q.answer}<span className="qa-when">{fmtWhen(q.answeredAt)}</span></div></div>
          </div>
        ))}
      </Card>
    </div>
  );
}

export function QAManager({ share, onChange }) {
  const [qs, setQs] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [pub, setPub] = useState({});
  const [busy, setBusy] = useState("");
  const [drafting, setDrafting] = useState("");
  const draft = async (q) => {
    setDrafting(q.id);
    try {
      const r = await shareApi.draftAnswer(share.id, q.id);
      setDrafts((d) => ({ ...d, [q.id]: r.draft }));
      toast.success("Draft ready — edit, then send");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not draft an answer"); }
    finally { setDrafting(""); }
  };
  const load = useCallback(() => shareApi.questionsAll(share.id).then((r) => { setQs(r); setDrafts(Object.fromEntries(r.map((q) => [q.id, q.answer || ""]))); setPub(Object.fromEntries(r.map((q) => [q.id, !!q.public]))); }).catch(() => {}), [share.id]);
  useEffect(() => { load(); }, [load]);

  const save = async (q) => {
    setBusy(q.id);
    try {
      const r = await shareApi.answer(share.id, q.id, { answer: drafts[q.id] || "", public: !!pub[q.id], notify: true });
      toast.success(r.notified ? `Answer saved and emailed to ${q.name}` : "Answer saved — visible in the room");
      load(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(""); }
  };
  const remove = async (q) => { await shareApi.deleteQuestion(share.id, q.id); toast.success("Question removed"); load(); onChange?.(); };
  const open = qs.filter((q) => !q.answer), done = qs.filter((q) => q.answer);

  const Row = ({ q }) => (
    <div className="qa-admin" key={q.id} data-testid={`qa-${q.id}`}>
      <div className="qa-admin-head">
        <span><b>{q.name}</b>{q.email ? <span className="ra-sub"> · {q.email}</span> : null}</span>
        <span className="ra-sub mono">{fmtWhen(q.createdAt)}</span>
      </div>
      <div className="qa-admin-q">{q.question}</div>
      <textarea className="inp" rows={drafts[q.id] ? 6 : 3} placeholder="Write your answer — or let AI draft one from the deal model…" value={drafts[q.id] ?? ""} onChange={(e) => setDrafts((d) => ({ ...d, [q.id]: e.target.value }))} data-testid={`qa-answer-${q.id}`} />
      <button className="btn btn-sm btn-share" style={{ marginTop: 6 }} disabled={drafting === q.id} onClick={() => draft(q)} data-testid={`qa-draft-${q.id}`}><Sparkles size={12} style={{ marginRight: 5 }} />{drafting === q.id ? "Drafting from deal model…" : drafts[q.id] ? "Re-draft with AI" : "Draft answer with AI"}</button>
      <div className="qa-admin-foot">
        <label className="qa-pub"><input type="checkbox" checked={!!pub[q.id]} onChange={() => setPub((p) => ({ ...p, [q.id]: !p[q.id] }))} data-testid={`qa-public-${q.id}`} /><Globe size={11} /> Publish answer to all investors</label>
        <span style={{ display: "flex", gap: 6 }}>
          <button className="icon-btn" title="Delete" onClick={() => remove(q)} data-testid={`qa-del-${q.id}`}><Trash2 size={11} /></button>
          <button className="btn btn-sm btn-primary" disabled={busy === q.id} onClick={() => save(q)} data-testid={`qa-save-${q.id}`}>{busy === q.id ? "Saving…" : q.answer ? "Update answer" : "Send answer"}</button>
        </span>
      </div>
      {q.answeredAt && <div className="ra-sub">Answered {fmtWhen(q.answeredAt)}{q.notifiedAt ? " · emailed to investor" : ""}{q.public ? " · published" : " · private"}</div>}
    </div>
  );

  return (
    <div className="grid g2" data-testid="qa-manager">
      <Card title={`Open Questions (${open.length})`} testid="qa-open">
        {open.length === 0 && <div className="bullet">No unanswered questions.</div>}
        {open.map((q) => <Row q={q} key={q.id} />)}
      </Card>
      <Card title={`Answered (${done.length})`} testid="qa-done">
        {done.length === 0 && <div className="bullet">Nothing answered yet.</div>}
        {done.map((q) => <Row q={q} key={q.id} />)}
      </Card>
    </div>
  );
}
